import React, { useState, useEffect } from 'react';
import {
  GraduationCap, FlaskConical, BookOpen, Users, Globe2,
  Award, TrendingUp, CheckCircle, ArrowRight, Brain,
  Lightbulb, Handshake, MapPin, Calendar, Star, BarChart3,
  Loader2,
} from 'lucide-react';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import api from '../utils/api';

/* ── Animated counter ─────────────────────────────────────────────────────── */
const Counter = ({ end, suffix = '', prefix = '', label, sub, icon: Icon, color }) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
  return (
    <div ref={ref} className={`rounded-2xl p-6 text-center ${color}`}>
      <div className="flex items-center justify-center mb-2">
        <Icon size={22} className="opacity-70 mr-2" />
        <span className="font-display text-4xl font-bold">
          {prefix}{inView ? <CountUp end={end} duration={2.5} separator="," /> : 0}{suffix}
        </span>
      </div>
      <div className="font-semibold text-sm mb-0.5">{label}</div>
      {sub && <div className="text-xs opacity-60 leading-relaxed">{sub}</div>}
    </div>
  );
};

/* ── Department icon map ──────────────────────────────────────────────────── */
const DEPT_META = {
  research:   { icon: Brain,         label: 'Research & Evaluation' },
  capacity:   { icon: GraduationCap, label: 'Capacity Development' },
  innovation: { icon: Lightbulb,     label: 'Social Innovation' },
  grants:     { icon: Handshake,     label: 'Grants & Partnerships' },
};

/* ── Timeline item ────────────────────────────────────────────────────────── */
const TimelineItem = ({ year, title, description, tags, left }) => (
  <div className={`flex gap-6 ${left ? 'flex-row' : 'flex-row-reverse'}`}>
    <div className="flex-1">
      <div className={`bg-white rounded-2xl p-6 shadow-card border border-gray-100 ${left ? 'text-left' : 'text-right'}`}>
        <div className={`flex items-center gap-2 mb-2 ${left ? '' : 'justify-end'}`}>
          <span className="px-3 py-1 bg-royal-50 text-royal-800 rounded-full text-xs font-bold">{year}</span>
        </div>
        <h3 className="font-display font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
        {tags?.length > 0 && (
          <div className={`flex flex-wrap gap-1.5 mt-3 ${left ? '' : 'justify-end'}`}>
            {tags.map(t => (
              <span key={t} className="px-2 py-0.5 bg-gold-50 text-gold-700 rounded-full text-[10px] font-semibold">{t}</span>
            ))}
          </div>
        )}
      </div>
    </div>
    <div className="flex flex-col items-center flex-shrink-0">
      <div className="w-4 h-4 bg-gold-500 rounded-full ring-4 ring-gold-200 z-10" />
      <div className="w-0.5 bg-gray-200 flex-1 mt-1" />
    </div>
    <div className="flex-1" />
  </div>
);

/* ── Case study card ──────────────────────────────────────────────────────── */
const CaseCard = ({ study }) => {
  const meta = DEPT_META[study.department] || DEPT_META.research;
  const Icon = meta.icon;
  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300">
      <div className="h-1.5 bg-royal-800" />
      <div className="p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-royal-50 flex items-center justify-center flex-shrink-0">
            <Icon size={18} className="text-royal-800" />
          </div>
          <div>
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{meta.label}</div>
            <h3 className="font-display font-semibold text-gray-900 text-sm leading-snug mt-0.5">{study.title}</h3>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-400 mb-4">
          <span className="flex items-center gap-1"><MapPin size={11} />{study.location}</span>
          <span className="flex items-center gap-1"><Calendar size={11} />{study.year}</span>
        </div>
        <ul className="space-y-1.5">
          {(study.outcomes || []).map((o, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
              <CheckCircle size={12} className="text-emerald-500 flex-shrink-0 mt-0.5" />{o}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const SDG_ALIGNMENT = [
  { num:'4',  label:'Quality Education',      desc:'Direct alignment through all training and evaluation programs.' },
  { num:'5',  label:'Gender Equality',        desc:'Girls\' education, women\'s leadership, and equity programs.' },
  { num:'8',  label:'Decent Work',            desc:'Vocational training and EdTech incubation for youth employment.' },
  { num:'10', label:'Reduced Inequalities',   desc:'Inclusive education programs targeting underserved populations.' },
  { num:'16', label:'Strong Institutions',    desc:'Evidence-based policy support for government education bodies.' },
  { num:'17', label:'Partnerships',           desc:'Multi-sectoral collaboration with NGOs, governments, and academia.' },
];

const SECONDARY_ICONS = {
  states_covered:   MapPin,
  countries_active: Globe2,
  partner_orgs:     Handshake,
  peer_papers:      Star,
  competency_gain:  TrendingUp,
  funds_efficiency: Award,
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════════════ */
export default function ImpactPage() {
  const [headlineMetrics,  setHeadlineMetrics]  = useState([]);
  const [secondaryMetrics, setSecondaryMetrics] = useState([]);
  const [geoDataset,       setGeoDataset]       = useState(null);
  const [caseStudies,      setCaseStudies]       = useState([]);
  const [milestones,       setMilestones]        = useState([]);
  const [loading,          setLoading]           = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/analytics/metrics?group=headline'),
      api.get('/analytics/metrics?group=secondary'),
      api.get('/analytics/datasets/projects_by_location').catch(() => ({ data: null })),
      api.get('/analytics/case-studies'),
      api.get('/analytics/milestones'),
    ]).then(([hRes, sRes, gRes, cRes, mRes]) => {
      setHeadlineMetrics(hRes.data);
      setSecondaryMetrics(sRes.data);
      setGeoDataset(gRes.data);
      setCaseStudies(cRes.data);
      setMilestones(mRes.data);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const HEADLINE_COLORS = [
    'bg-royal-800 text-white',
    'bg-gold-500 text-royal-950',
    'bg-royal-800 text-white',
    'bg-gold-500 text-royal-950',
  ];
  const HEADLINE_ICONS = [GraduationCap, FlaskConical, BookOpen, Users];

  const [statsRef, statsInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [caseRef,  caseInView]  = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <div className="min-h-screen bg-gray-50 font-body">
      <Navbar />
      <div className="pt-20">

        {/* Hero */}
        <section className="relative bg-hero-gradient py-16 overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIwMjA2MCIgc3Ryb2tlLXdpZHRoPSIwLjUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] " />
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="max-w-3xl">
              <span className="section-tag border-gold-400/30 text-gold-300 bg-gold-500/10 mb-5">
                <TrendingUp size={13} /> Impact & Metrics
              </span>
              <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                Our Impact in Numbers
              </h1>
              <p className="text-white/60 text-lg leading-relaxed">
                Concrete, verified metrics from SAAI Institute's programs across Africa — all data managed from our admin dashboard.
              </p>
            </div>
          </div>
        </section>

        {/* ── Headline Metrics ── */}
        <section ref={statsRef} className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-10">
              <span className="section-tag mb-3">Key Metrics</span>
              <h2 className="font-display text-3xl font-bold text-gray-900">Impact at a Glance</h2>
              <p className="text-gray-500 text-sm mt-2 max-w-xl mx-auto">All figures verified through SAAI's internal M&E framework and third-party evaluations.</p>
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 size={28} className="animate-spin text-royal-800" /></div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                  {headlineMetrics.map((m, i) => (
                    <Counter
                      key={m._id}
                      end={m.value}
                      suffix={m.suffix || ''}
                      prefix={m.prefix || ''}
                      label={m.label}
                      sub={m.description}
                      icon={HEADLINE_ICONS[i % 4]}
                      color={HEADLINE_COLORS[i % 4]}
                    />
                  ))}
                </div>

                {/* Secondary metrics */}
                {secondaryMetrics.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {secondaryMetrics.map(m => {
                      const Icon = SECONDARY_ICONS[m.key] || Award;
                      return (
                        <div key={m._id} className="bg-gray-50 rounded-xl p-4 text-center border border-gray-100">
                          <Icon size={16} className="text-royal-800 mx-auto mb-1.5" />
                          <div className="font-display font-bold text-xl text-royal-800">
                            {m.prefix || ''}{m.value >= 1000 ? m.value.toLocaleString() : m.value}{m.suffix || ''}
                          </div>
                          <div className="text-xs text-gray-500 leading-tight mt-0.5">{m.label}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* ── Geographic reach ── */}
        {geoDataset && (
          <section className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <span className="section-tag mb-4">Geographic Reach</span>
                  <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">Projects by Location</h2>
                  <p className="text-gray-500 text-sm leading-relaxed mb-6">SAAI maintains active programs across 12+ Nigerian states, with Abuja serving as the primary research hub.</p>
                  <div className="space-y-2">
                    {[['Nigeria','Abuja, Lagos, Kano, Kaduna, Katsina, Oyo, Osun, Ekiti, Borno, Adamawa, Rivers, Enugu'],['Africa','Banjul, Upper River Region, North Bank Region']].map(([c,s]) => (
                      <div key={c} className="flex items-start gap-3 p-3 bg-white rounded-xl border border-gray-100">
                        <Globe2 size={16} className="text-royal-800 flex-shrink-0 mt-0.5" />
                        <div><div className="font-semibold text-sm text-gray-900">{c}</div><div className="text-xs text-gray-500 mt-0.5">{s}</div></div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-6">
                  <h3 className="font-display font-semibold text-gray-900 mb-1 text-sm">{geoDataset.label}</h3>
                  <p className="text-xs text-gray-400 mb-5">{geoDataset.description}</p>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={geoDataset.dataPoints?.map(p => ({ name: p.label, Projects: p.value, color: p.color }))} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={85} />
                      <Tooltip contentStyle={{ background:'#0f0d36', border:'1px solid rgba(245,197,24,0.2)', borderRadius:8, color:'#fff', fontSize:12 }} formatter={v => [`${v} projects`]} />
                      <Bar dataKey="Projects" radius={[0,6,6,0]}>
                        {geoDataset.dataPoints?.map((entry, i) => <Cell key={i} fill={entry.color || '#2B3A8F'} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── Case Studies ── */}
        <section ref={caseRef} className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="section-tag mb-4">Case Studies</span>
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-3">Active & Completed Projects</h2>
              <p className="text-gray-500 max-w-xl mx-auto text-sm">Detailed outcomes from funded interventions across our four departments.</p>
            </div>
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 size={28} className="animate-spin text-royal-800" /></div>
            ) : caseStudies.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <BookOpen size={36} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">No case studies yet. Add them in Admin → Analytics.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
                {caseStudies.map((study, i) => (
                  <div key={study._id} className={caseInView ? 'animate-fade-up' : 'opacity-0'} style={{ animationDelay:`${i*80}ms` }}>
                    <CaseCard study={study} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ── SDG Alignment ── */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-10">
              <span className="section-tag mb-4">Global Alignment</span>
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-3">UN Sustainable Development Goals</h2>
              <p className="text-gray-500 max-w-xl mx-auto text-sm">SAAI's work directly contributes to six of the 17 UN SDGs, reinforcing our mandate to international funders and policy bodies.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {SDG_ALIGNMENT.map(sdg => (
                <div key={sdg.num} className="bg-white rounded-2xl p-5 shadow-card border border-gray-100 flex items-start gap-4">
                  <div className="w-12 h-12 bg-royal-800 rounded-xl flex items-center justify-center text-gold-400 font-display font-bold text-lg flex-shrink-0">{sdg.num}</div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">SDG {sdg.num}: {sdg.label}</div>
                    <div className="text-xs text-gray-500 mt-1 leading-relaxed">{sdg.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Milestones Timeline ── */}
        <section className="py-16 bg-white overflow-hidden">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="section-tag mb-4">Our Journey</span>
              <h2 className="font-display text-3xl font-bold text-gray-900">Milestones Since {milestones[0]?.year || '2010'}</h2>
            </div>
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 size={28} className="animate-spin text-royal-800" /></div>
            ) : milestones.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">No milestones yet. Add them in Admin → Analytics.</div>
            ) : (
              <div className="space-y-0">
                {milestones.map((m, i) => <TimelineItem key={m._id} {...m} left={i % 2 === 0} />)}
              </div>
            )}
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-16 bg-hero-gradient relative overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIwMjA2MCIgc3Ryb2tlLXdpZHRoPSIwLjUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] " />
          <div className="relative max-w-3xl mx-auto px-6 text-center">
            <h2 className="font-display text-3xl font-bold text-white mb-4">Commission Research or Fund an Initiative</h2>
            <p className="text-white/60 mb-8 leading-relaxed">Our impact data demonstrates SAAI's capacity for rigorous, evidence-based research or change. Partner with us to scale proven initiatives.</p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link to="/partner" className="btn-primary px-8 py-3.5"><Handshake size={16} /> Partner With Us</Link>
              <Link to="/insights" className="btn-outline-white px-8 py-3.5"><BarChart3 size={16} /> View Insights Portal</Link>
            </div>
          </div>
        </section>

      </div>
      <Footer />
    </div>
  );
}